import { useEffect } from 'react'
import { pushProducaoOutbox } from './producaoSync'
import { pushCarreteiroOutbox } from './carreteiroSync'
import { pushParteDiariaOutbox } from './parteDiariaSync'
import { pushFotoOutbox } from './fotoSync'
import { refreshAllCatalogs } from './catalogSync'
import { aquecerOffline } from './offlineWarmup'
import { isBackendReachable } from '@/core/network/onlineProbe'
import { sessionStore } from '@/core/auth/SessionStore'
import { isNative } from '@/core/platform/runtime'

/**
 * Foreground sync engine. Dispara em vários eventos do navegador:
 *  - `online` — voltou a internet
 *  - `visibilitychange visible` — usuário voltou ao app (alt+tab, foreground)
 *  - `pageshow` com `event.persisted` — bfcache restore (iOS Safari)
 *  - `focus` na window
 *  - heartbeat 60s enquanto app está visível (iOS Safari não tem Background Sync)
 *
 * Para cada disparo: faz HEAD probe, e se passar, executa os 3 pushes em sequência
 * + refresh de catálogos. Mutex em cada `push*` evita corrida.
 *
 * Para evitar storm em rotas como /capture (onde a câmera consome muito CPU),
 * o intervalo do heartbeat aumenta quando o último push foi recente.
 */

// Heartbeat mais agressivo em native: APK WebView é pior em propagar eventos
// `visibilitychange`/`focus`/`online` — confiamos mais no heartbeat + nos
// listeners nativos (App.appStateChange, Network.networkStatusChange).
const HEARTBEAT_MS = isNative() ? 30_000 : 60_000
const COOLDOWN_MS = 20_000 // só roda novo ciclo se passaram pelo menos 20s do último
const LAST_SYNC_KEY = 'monitor.lastSyncAt'

let lastRunAt = 0
let runningPromise: Promise<void> | null = null

/** Snapshot síncrono pra o hook useSyncStatus saber se há ciclo rodando. */
export function isSyncRunning(): boolean {
  return runningPromise != null
}

/** Lê o timestamp do último ciclo bem-sucedido (persistido em localStorage). */
export function getLastSyncAt(): number | null {
  try {
    const raw = localStorage.getItem(LAST_SYNC_KEY)
    return raw ? Number(raw) : null
  } catch {
    return null
  }
}

export interface TriggerOpts {
  /** Pula o cooldown de 20s. Usado em post-save pra UI feedback imediato. */
  ignoreCooldown?: boolean
}

export async function triggerForegroundSync(
  reason: string,
  opts: TriggerOpts = {},
): Promise<void> {
  const now = Date.now()
  if (!opts.ignoreCooldown && now - lastRunAt < COOLDOWN_MS && runningPromise) {
    // já tem ciclo recente em andamento — junta-se a ele
    return runningPromise
  }
  if (!sessionStore.isAuthenticated()) return
  // Servidor já recusou este token: não adianta martelar (23 requests → 23x 401
  // a cada heartbeat). O outbox espera o operador entrar de novo.
  if (sessionStore.serverRejectedToken()) return
  if (!(await isBackendReachable())) return

  lastRunAt = now
  window.dispatchEvent(new CustomEvent('monitor:sync:start', { detail: { reason } }))
  runningPromise = doSync(reason).finally(() => {
    runningPromise = null
    try {
      localStorage.setItem(LAST_SYNC_KEY, String(Date.now()))
    } catch {
      /* storage cheio / privacy mode */
    }
    window.dispatchEvent(new CustomEvent('monitor:sync:end', { detail: { reason } }))
  })
  return runningPromise
}

async function doSync(reason: string): Promise<void> {
  console.info('[fgSync] ciclo disparado por:', reason)
  // Push primeiro — descarrega outbox enquanto temos rede.
  const [prodRes, carrRes, fotoRes, pdRes] = await Promise.allSettled([
    pushProducaoOutbox(),
    pushCarreteiroOutbox(),
    pushFotoOutbox(),
    pushParteDiariaOutbox(),
  ])
  let acceptedProd = 0
  let acceptedFoto = 0
  let rejectedProd = 0
  let hadNetworkError = false
  if (prodRes.status === 'fulfilled') {
    acceptedProd = prodRes.value?.accepted ?? 0
    rejectedProd = prodRes.value?.rejected ?? 0
    if (prodRes.value?.networkError) hadNetworkError = true
  } else {
    console.warn('[fgSync] push produção falhou:', prodRes.reason)
  }
  if (carrRes.status === 'rejected') {
    console.warn('[fgSync] push carreteiro falhou:', carrRes.reason)
  }
  if (pdRes.status === 'rejected') {
    console.warn('[fgSync] push parte diária falhou:', pdRes.reason)
  }
  if (fotoRes.status === 'fulfilled') {
    acceptedFoto = fotoRes.value?.accepted ?? 0
    // fotoSync mistura falhas de rede com rejeições do servidor em `failed`;
    // não fazemos toast por isso para evitar falso-positivo offline.
  } else {
    console.warn('[fgSync] push fotos falhou:', fotoRes.reason)
  }

  const totalAccepted = acceptedProd + acceptedFoto
  // Notifica o app só quando algo concreto aconteceu — silêncio = não polui UI.
  if (totalAccepted > 0) {
    const partes = [
      acceptedProd > 0 ? `${acceptedProd} produção` : null,
      acceptedFoto > 0 ? `${acceptedFoto} foto` : null,
    ].filter(Boolean)
    dispatchToast('ok', `${partes.join(' + ')} enviado(s) ✓`)
  }
  if (rejectedProd > 0 && !hadNetworkError) {
    dispatchToast('bad', `${rejectedProd} produção rejeitada(s) pelo servidor`)
  }
  // hadNetworkError não vira toast — banner offline já comunica isso.

  // Refresh de catálogos em segundo plano (não bloqueia). Depois deles, a
  // pré-carga offline (o Carreteiro lê as obras do catálogo recém-baixado).
  refreshAllCatalogs()
    .catch((e) => console.warn('[fgSync] catalog refresh falhou:', e))
    .then(() => aquecerOffline())
    .catch((e) => console.warn('[fgSync] pré-carga offline falhou:', e))
}

function dispatchToast(kind: 'ok' | 'warn' | 'bad' | 'info', message: string) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent('monitor:toast', { detail: { kind, message } }))
}

/**
 * Hook React que registra todos os listeners enquanto montado.
 * Deve ser usado em alta na árvore (App.tsx) APÓS o usuário estar autenticado.
 *
 * Em Capacitor native, adiciona 2 listeners equivalentes via plugins:
 *  - `@capacitor/app` appStateChange → sync ao voltar do background do Android
 *  - `@capacitor/network` networkStatusChange → sync quando rede volta
 * Esses são mais confiáveis que `visibilitychange`/`online` do browser dentro
 * do WebView.
 */
export function useForegroundSyncListeners(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return

    const onOnline = () => void triggerForegroundSync('online')
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void triggerForegroundSync('visibility')
    }
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) void triggerForegroundSync('bfcache-restore')
    }
    const onFocus = () => void triggerForegroundSync('focus')

    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pageshow', onPageShow)
    window.addEventListener('focus', onFocus)

    // Heartbeat — só roda quando visível para não drenar bateria.
    // Em Capacitor, "visible" pode ficar travado em "hidden" depois de algumas
    // transições; tratamos `document.hidden === false` OU isNative()
    // (assumindo que App.appStateChange já filtrou foreground/background).
    const heartbeat = setInterval(() => {
      const visible = document.visibilityState === 'visible' || (isNative() && !document.hidden)
      if (visible) void triggerForegroundSync('heartbeat')
    }, HEARTBEAT_MS)

    // Capacitor native — handles ficam aqui pra cleanup.
    // Promises porque addListener é async em Capacitor 6+.
    type Removable = { remove: () => Promise<void> } | null
    let appHandle: Removable = null
    let netHandle: Removable = null
    if (isNative()) {
      void (async () => {
        try {
          const { App } = await import('@capacitor/app')
          appHandle = await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) void triggerForegroundSync('native-app-resume', { ignoreCooldown: true })
          })
        } catch (err) {
          console.warn('[fgSync] @capacitor/app indisponível:', err)
        }
        try {
          const { Network } = await import('@capacitor/network')
          netHandle = await Network.addListener('networkStatusChange', ({ connected }) => {
            if (connected) void triggerForegroundSync('native-network-up', { ignoreCooldown: true })
          })
        } catch (err) {
          console.warn('[fgSync] @capacitor/network indisponível:', err)
        }
      })()
    }

    // Dispara um ciclo inicial ao montar
    void triggerForegroundSync('mount')

    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('focus', onFocus)
      clearInterval(heartbeat)
      void appHandle?.remove().catch(() => {})
      void netHandle?.remove().catch(() => {})
    }
  }, [enabled])
}
